import React, { useState } from 'react';

import { FilterRange, Input } from '@hcs/design-system';
import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateEditCallbackArgs,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import {
  formatMoneyPerMonth,
  formatNumber,
  numInputStrStrip,
  unformat,
} from '@hcs/utils';

const FIELD = PropertyStateFields.lastClosePriceRental;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Lease Price';
const labelShort = 'Lease $';
const propertyStatePath: PropertyStatePaths =
  '/complexFieldsRental/lastClosePrice';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[PropertyStateFields.lastClosePriceRental];
  } else if (propertyStateType === 'Core') {
    return propertyState?.complexFieldsRental?.lastClosePrice;
  }
  return undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatMoneyPerMonth(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `Leased for ${formatValue(propertyStateArgs)}`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateArgsProps & {
  onChange: (args: PropertyStateEditCallbackArgs<typeof FIELD>) => void;
  className?: string;
}) => {
  const defaultValue = getValue(propertyStateArgs);
  const [value, setValue] = useState(formatNumber(defaultValue));

  return (
    <Input
      dataHcName={`edit-${FIELD}`}
      ariaLabel={`Adjust ${label}`}
      className={className}
      value={value?.toString() || ''}
      onChange={(value) => setValue(numInputStrStrip(value))}
      onFocus={() => {
        setValue(unformat(value).toString());
      }}
      onBlur={() => {
        setValue(formatNumber(Number(value)));
        onChange({
          path: propertyStatePath,
          value: Number(value),
          field: FIELD,
        });
      }}
      maxLength={25}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
  hideLabels,
}: PropertyStateFilterProps<typeof FIELD>) => {
  return (
    <FilterRange
      className={className}
      hideLabels={hideLabels}
      disabled={disabled}
      maxVal={999999}
      initialValue={{
        min: spatialFiltersInput.minLeasePrice
          ? formatMoneyPerMonth(spatialFiltersInput.minLeasePrice)
          : '',
        max: spatialFiltersInput.maxLeasePrice
          ? formatMoneyPerMonth(spatialFiltersInput.maxLeasePrice)
          : '',
      }}
      onBlur={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            minLeasePrice: value.min === '' ? null : Number(value.min),
            maxLeasePrice: value.max === '' ? null : Number(value.max),
          },
        });
      }}
      dataHcName={`${FIELD}-filter`}
    />
  );
};

export const LAST_CLOSE_PRICE_RENTAL_CONFIG: FieldConfig = {
  field: FIELD,
  labelShort,
  label,
  propertyStatePath,
  Filter,
  getValue,
  formatValue,
  formatInline,
  HeaderCell,
  ContentCell,
  Display,
  Edit,
};
